// TikTokPostForm.tsx — (tf3-26; planning 10-15 00:15) posting a delivered video to ONE TikTok account, exactly as
// TikTok's Content Sharing Guidelines ask (the rules are lib/tiktok-post.ts, tested there). Nothing goes to TikTok until
// the creator presses Post: opening the form reads the account's settings (a fresh creator_info, through the package's
// route); the post itself is the app's Post (POST /api/videos/:id/post) with this target's choices.
import { useEffect, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { publishingApi, type PublishChannelView } from "@/api/videos.ts";
import { skipText } from "@/lib/post-result.ts";
import { Btn, TfSpinner } from "@/components/v2/primitives.tsx";
import { w } from "@/lib/words.ts";   // (B107) TikTok's lines, editable (the guideline ones locked) in Admin → Words
import {
  initialFormState, prefillTitle, whyNotYet, choicesOf, afterPost, privacyLabel, privacyOptionDisabled, consentLine, disclosureLabel, lengthOf,
  TITLE_MAX, POLL_MS, UNREADABLE, type TikTokFormState, type VideoLength,
} from "@/lib/tiktok-post.ts";

const row = { display: "flex", gap: 8, alignItems: "center", padding: "4px 0" } as const;
const muted = { color: "var(--tf-fg-muted, #666)", fontSize: 13 } as const;

/** `held`: (audit 10-15) why this video can't be posted now at all (a newer cut being finished) — the button stays off. */
export function TikTokPostForm({ videoId, channel, videoUrl, initialTitle, onPosted, held = null }: {
  videoId: string; channel: PublishChannelView; videoUrl: string | null; initialTitle: string; onPosted?: () => void; held?: string | null;
}) {
  // a fresh read each time the form opens — never a cached one (the account's settings can change in the app). Not
  //  re-read on window focus: a re-read mid-form must not swap the form out from under the creator
  const info = useQuery({ queryKey: ["tiktok-creator-info", channel.channelId], queryFn: () => publishingApi.creatorInfo(channel.channelId),
    staleTime: 0, gcTime: 0, refetchOnMount: "always", refetchOnWindowFocus: false, retry: false });
  const [s, setS] = useState<TikTokFormState>(() => initialFormState(initialTitle));
  // (tf3-27; C1.6b handoff §2.2) the title starts as the TikTok version's approved caption, fitted to TikTok — once, and
  //  only while the creator hasn't typed in it. It stays editable: what is left in the field is what's posted.
  const prefill = useQuery({ queryKey: ["tiktok-title", videoId], queryFn: () => publishingApi.tiktokTitle(videoId),
    staleTime: 0, gcTime: 0, retry: false, refetchOnWindowFocus: false });
  const typed = useRef(false);
  useEffect(() => {
    const t = prefill.data?.title;
    if (t && !typed.current) setS((prev) => ({ ...prev, title: prefillTitle(t) }));
  }, [prefill.data?.title]);
  // (audit 10-15) no file → unreadable at once, never "checking" for ever
  const [durationSec, setDurationSec] = useState<VideoLength>(videoUrl ? null : UNREADABLE);
  const set = (p: Partial<TikTokFormState>) => setS((prev) => ({ ...prev, ...p }));
  const reasons = whyNotYet(info.data ?? null, s, durationSec, held);

  const post = useMutation({
    mutationFn: () => publishingApi.postLater(videoId, [{ channelId: channel.channelId, channel: "tiktok", tiktok: choicesOf(info.data!, s) }]),
    onSuccess: () => onPosted?.(),
  });
  const postId = post.data?.postId ?? null;
  const qc = useQueryClient();
  // (audit 10-15) the workers took the post but sent nothing to TikTok (no postId): their reasons, and the form stays
  const notSent = post.isSuccess && !postId ? (post.data?.skipped ?? []).map((k) => skipText(k.reason)) : [];
  // after posting: TikTok processes the video for a few minutes — the target's status, as it changes; (audit 10-15) at
  //  most 10 minutes of asking, and an unreadable status or a post with no TikTok target stops it, named
  const status = useQuery({ queryKey: ["tiktok-post-status", postId], queryFn: () => publishingApi.postStatus(videoId, postId!),
    enabled: !!postId, retry: false, refetchOnWindowFocus: false,
    refetchInterval: (q) => afterPost(q.state.data, q.state.error ? String((q.state.error as { message?: string }).message) : null,
      channel.channelId, q.state.dataUpdateCount, s.privacyLevel).again ? POLL_MS : false });
  const after = afterPost(status.data, status.error ? String((status.error as { message?: string }).message) : null,
    channel.channelId, qc.getQueryState(["tiktok-post-status", postId])?.dataUpdateCount ?? 0, s.privacyLevel);
  const ci = info.data;
  const header = ci && (
    <div style={row}>
      {ci.avatarUrl && <img src={ci.avatarUrl} alt="" width={28} height={28} style={{ borderRadius: "50%" }} />}
      <strong>{w("page.tiktok.post_as", { account: ci.nickname })}</strong>
    </div>
  );

  // (audit 10-15) once posted, the status comes first: a later read of the account's settings never hides it
  if (postId) return (
    <div style={{ border: "1px solid var(--tf-rule)", borderRadius: 8, padding: 12, margin: "8px 0" }}>
      {header}
      <p>{w("page.tiktok.processing_notice")}</p>
      <p role="status">{after.text}</p>
    </div>
  );
  if (info.isLoading) return <div style={row}><TfSpinner size={14} /> <span style={muted}>{w("page.tiktok.reading_settings")}</span></div>;
  if (info.error || !ci) return <p style={{ color: "var(--tf-danger, #c00)" }}>{w("page.tiktok.settings_unread", { reason: (info.error as { message?: string } | null)?.message ?? w("page.tiktok.no_answer") })}</p>;

  return (
    <div style={{ border: "1px solid var(--tf-rule)", borderRadius: 8, padding: 12, margin: "8px 0" }}>
      {header}

      {!ci.canPost ? (
        <p role="alert" style={{ color: "var(--tf-danger, #c00)" }}>{reasons[0]}</p>
      ) : (
        <>
          <video src={videoUrl ?? undefined} controls preload="metadata" style={{ width: "100%", maxWidth: 320, borderRadius: 8 }}
            onLoadedMetadata={(e) => setDurationSec(lengthOf(e.currentTarget.duration))} onError={() => setDurationSec(UNREADABLE)} />

          <label style={{ display: "block", marginTop: 8 }}>
            <span style={muted}>{w("page.tiktok.title")}</span>
            <textarea value={s.title} maxLength={TITLE_MAX} rows={3} style={{ width: "100%" }} onChange={(e) => { typed.current = true; set({ title: e.target.value }); }} />
            <span style={muted}>{s.title.length} / {TITLE_MAX}</span>
          </label>

          <label style={row}>
            <span>{w("page.tiktok.who_can_see")}</span>
            <select value={s.privacyLevel} onChange={(e) => set({ privacyLevel: e.target.value })} aria-label={w("page.tiktok.who_can_see")}>
              <option value="" disabled>{w("page.tiktok.choose")}</option>
              {ci.privacyLevelOptions.map((o) => (
                <option key={o} value={o} disabled={privacyOptionDisabled(o, s)}
                  title={privacyOptionDisabled(o, s) ? w("page.tiktok.branded_not_private") : undefined}>{privacyLabel(o)}</option>
              ))}
            </select>
          </label>

          {([["allowComment", w("page.tiktok.allow_comments"), ci.commentDisabled], ["allowDuet", w("page.tiktok.allow_duet"), ci.duetDisabled], ["allowStitch", w("page.tiktok.allow_stitch"), ci.stitchDisabled]] as const)
            .map(([k, label, off]) => (
              <label key={k} style={{ ...row, opacity: off ? 0.5 : 1 }}>
                <input type="checkbox" checked={!off && s[k]} disabled={off} onChange={(e) => set({ [k]: e.target.checked })} />
                {label}{off && <span style={muted}> {w("page.tiktok.interaction_off")}</span>}
              </label>
            ))}

          <label style={row}>
            <input type="checkbox" checked={s.disclose} onChange={(e) => set({ disclose: e.target.checked, ...(e.target.checked ? {} : { yourBrand: false, brandedContent: false }) })} />
            {w("page.tiktok.disclose")} <span style={muted}>{w("page.tiktok.disclose_note")}</span>
          </label>
          {s.disclose && (
            <div style={{ paddingLeft: 24 }}>
              <label style={row}>
                <input type="checkbox" checked={s.yourBrand} onChange={(e) => set({ yourBrand: e.target.checked })} />
                {w("page.tiktok.your_brand")} <span style={muted}>{w("page.tiktok.your_brand_note")}</span>
              </label>
              <label style={row}>
                <input type="checkbox" checked={s.brandedContent} onChange={(e) => set({ brandedContent: e.target.checked })} />
                {w("page.tiktok.branded")} <span style={muted}>{w("page.tiktok.branded_note")}</span>
              </label>
              {disclosureLabel(s) && <p style={muted}>{disclosureLabel(s)}</p>}
            </div>
          )}

          {reasons.length > 0 && <ul style={{ ...muted, margin: "8px 0" }}>{reasons.map((r) => <li key={r}>{r}</li>)}</ul>}
          <p style={muted}>
            {consentLine(s).text}{" "}
            {consentLine(s).links.map(([label, url], i) => (
              <span key={url}>{i > 0 && ` ${w("page.tiktok.consent_and")} `}<a href={url} target="_blank" rel="noreferrer">{label}</a></span>
            ))}.
          </p>
          <Btn onClick={() => post.mutate()} disabled={reasons.length > 0 || post.isPending} loading={post.isPending}>{w("page.tiktok.post")}</Btn>
          {post.error && <p style={{ color: "var(--tf-danger, #c00)" }}>{(post.error as { message?: string }).message}</p>}
          {notSent.length > 0 && <div role="alert" style={{ color: "var(--tf-danger, #c00)" }}>
            <p style={{ margin: "8px 0 0" }}>{w("page.tiktok.nothing_sent")}</p>
            <ul style={{ margin: 0 }}>{notSent.map((r) => <li key={r}>{r}</li>)}</ul>
          </div>}
        </>
      )}
    </div>
  );
}
